import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { summaryCardTheme as t } from "./styles";

const SEVERITY_ORDER = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];

function SummarySeverityChart({ securityFailedBySeverity, onSeverityClick }) {
  const data = useMemo(() => {
    const bySeverity = securityFailedBySeverity || {};
    const total = Object.values(bySeverity).reduce((a, b) => a + b, 0) || 1;
    return SEVERITY_ORDER.filter((s) => (bySeverity[s] || 0) > 0).map(
      (name) => ({
        name,
        value: bySeverity[name] || 0,
        fill: t.colors.severity[name] || t.colors.chart[3],
        pct: total ? Math.round(((bySeverity[name] || 0) / total) * 100) : 0,
      })
    );
  }, [securityFailedBySeverity]);

  const totalChecks = data.reduce((a, d) => a + d.value, 0);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    return (
      <Box
        sx={{
          ...t.chartTooltip,
          fontFamily: t.fontFamily,
        }}
      >
        <Typography component="span" sx={{ fontWeight: 600, color: t.colors.text, fontSize: "0.75rem" }}>
          {d.name}
        </Typography>
        <Typography component="span" sx={{ color: t.colors.textMuted, fontSize: "0.75rem", ml: 0.5 }}>
          — {d.value} checks ({d.pct}%)
        </Typography>
      </Box>
    );
  };

  if (data.length === 0) {
    return (
      <Box
        sx={{
          height: 200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: t.radius.lg,
          border: `1px solid ${t.colors.border}`,
          backgroundColor: t.colors.card,
          fontFamily: t.fontFamily,
        }}
      >
        <Typography variant="body2" sx={{ color: t.colors.textMuted }}>
          No security failed checks by severity
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: t.colors.card,
        border: `1px solid ${t.colors.border}`,
        borderRadius: t.radius.lg,
        p: 2,
        boxShadow: t.shadow,
        fontFamily: t.fontFamily,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: t.colors.text, fontSize: "0.875rem", mb: 1, fontFamily: t.fontFamily }}>
        Security
      </Typography>
      <Typography variant="body2" sx={{ color: t.colors.textMuted, fontSize: "0.75rem", mb: 2, fontFamily: t.fontFamily }}>
        Total: {totalChecks} failed checks
      </Typography>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
          onClick={(state) => {
            if (!onSeverityClick || !state?.activePayload?.length) return;
            const sev = state.activePayload[0]?.payload?.name;
            if (sev) onSeverityClick(sev);
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={t.colors.borderLight} horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11, fill: t.colors.textMuted }} />
          <YAxis type="category" dataKey="name" width={56} tick={{ fontSize: 11, fill: t.colors.text }} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: t.colors.background }} />
          <Bar
            dataKey="value"
            nameKey="name"
            radius={[0, 4, 4, 0]}
            maxBarSize={24}
            cursor={onSeverityClick ? "pointer" : "default"}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

export default SummarySeverityChart;
